import React, { useMemo, useState } from "react";
import { ChevronRight, Calculator } from "lucide-react";
import { Link } from "react-router-dom";
import {
  VEHICLE_SIZES, DETAIL_PACKAGES, INTERIOR_PACKAGES, EXTERIOR_PACKAGES,
  PAINT_CORRECTION, INTERIOR_ADDONS, EXTERIOR_ADDONS,
} from "../../lib/content";

const ALL_PACKAGES = [
  ...DETAIL_PACKAGES.map((p) => ({ ...p, group: "Full Detail" })),
  ...INTERIOR_PACKAGES.map((p) => ({ ...p, group: "Interior" })),
  ...EXTERIOR_PACKAGES.map((p) => ({ ...p, group: "Exterior" })),
  ...PAINT_CORRECTION.map((p) => ({ ...p, group: "Paint Correction" })),
];

export const PriceEstimator = ({ defaultPackage, onEstimate }) => {
  const [sizeId, setSizeId] = useState("small");
  const [pkgId, setPkgId] = useState(defaultPackage || "signature");
  const [addonIds, setAddonIds] = useState([]);

  const size = VEHICLE_SIZES.find((s) => s.id === sizeId);
  const pkg = ALL_PACKAGES.find((p) => p.id === pkgId);
  const addonTotal = useMemo(
    () => addonIds.reduce((sum, id) => {
      const a = [...INTERIOR_ADDONS, ...EXTERIOR_ADDONS].find((x) => x.id === id);
      return sum + (a ? a.price : 0);
    }, 0),
    [addonIds]
  );
  const total = (pkg?.base || 0) + (size?.modifier || 0) + addonTotal;

  const toggleAddon = (id) => setAddonIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  React.useEffect(() => {
    if (onEstimate) onEstimate({ total, size, pkg, addonIds });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, sizeId, pkgId, addonIds.join(",")]);

  return (
    <div data-testid="price-estimator" className="glass-card p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Calculator size={20} className="text-silver" />
        <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-white">Price Estimator</h3>
      </div>

      <div className="space-y-6">
        <div>
          <label className="overline block mb-3">Vehicle Size</label>
          <div className="grid grid-cols-3 gap-2">
            {VEHICLE_SIZES.map((s) => (
              <button key={s.id} onClick={() => setSizeId(s.id)} data-testid={`est-size-${s.id}`}
                className={`matte p-3 text-left transition-colors ${sizeId === s.id ? "border-silver bg-white/5" : "hover:border-white/20"}`}>
                <p className="font-display font-bold text-sm text-white">{s.label}</p>
                <p className="text-gray-500 text-xs mt-1">{s.description}</p>
                <p className="font-mono-tech text-silver text-xs mt-2">{s.modifier === 0 ? "Base" : `+$${s.modifier}`}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="overline block mb-3">Package</label>
          <select value={pkgId} onChange={(e) => setPkgId(e.target.value)} data-testid="est-package"
            className="w-full bg-[#0F0F0F] border border-white/15 text-white px-3 h-12 rounded-none font-mono-tech text-sm focus:outline-none focus:ring-1 focus:ring-silver">
            {["Full Detail", "Interior", "Exterior", "Paint Correction"].map((g) => (
              <optgroup key={g} label={g} className="bg-[#0F0F0F]">
                {ALL_PACKAGES.filter((p) => p.group === g).map((p) => (
                  <option key={p.id} value={p.id}>{p.name} — ${p.base}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div>
          <label className="overline block mb-3">Interior Add-Ons</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {INTERIOR_ADDONS.map((a) => (
              <label key={a.id} data-testid={`est-addon-${a.id}`}
                className={`matte px-4 py-3 cursor-pointer flex items-start gap-3 transition-colors ${addonIds.includes(a.id) ? "border-silver bg-white/5" : "hover:border-white/20"}`}>
                <input type="checkbox" checked={addonIds.includes(a.id)} onChange={() => toggleAddon(a.id)} className="mt-1 accent-silver" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white leading-tight">{a.name}</p>
                  <p className="font-mono-tech text-[11px] text-silver mt-0.5">{a.display}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="overline block mb-3">Exterior Add-Ons</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {EXTERIOR_ADDONS.map((a) => (
              <label key={a.id} data-testid={`est-addon-${a.id}`}
                className={`matte px-4 py-3 cursor-pointer flex items-start gap-3 transition-colors ${addonIds.includes(a.id) ? "border-silver bg-white/5" : "hover:border-white/20"}`}>
                <input type="checkbox" checked={addonIds.includes(a.id)} onChange={() => toggleAddon(a.id)} className="mt-1 accent-silver" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white leading-tight">{a.name}</p>
                  <p className="font-mono-tech text-[11px] text-silver mt-0.5">{a.display}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="overline">Estimated Starting Total</p>
            <p className="font-mono-tech text-4xl font-semibold text-white mt-1" data-testid="est-total">${total.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-2 max-w-md">Final pricing depends on vehicle condition and inspection.</p>
          </div>
          <Link to="/contact" state={{ estimate: { total, pkg: pkg?.name, size: size?.label, addonIds } }}
            data-testid="est-book"
            className="bg-silver hover:bg-white text-black px-6 py-3.5 uppercase tracking-widest text-xs font-bold transition-colors inline-flex items-center gap-2">
            Book This <ChevronRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
};
